import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference } from '@/helpers';
import { PageHeader } from '@/modules/common';
import LoadingContainer from '@/modules/common/components/loading-container';
import { Routes } from '@/router/routes';

import { useStyleDetails } from '../api';
import { StyleForm } from '../components';
import { StyleItem } from '../types';
import { StyleFormValues } from '../validations';

export const StyleDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();

  const { details, getUploadUrlDataMutation, isFetching, updateStyleMutation } =
    useStyleDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      ...details,
      loraModelTriggerWord: details.loraModelTriggerWord || '',
      loraModelStrength: details.loraModelStrength || undefined,
      updatedAt: format(new Date(details.updatedAt), 'yyyy-MM-dd'),
      loraEnabled: false,
    };
  }, [details]);

  const updateModel = async (values: StyleFormValues) => {
    try {
      if (values.imagePublicUrl instanceof File) {
        await getUploadUrlDataMutation.mutateAsync({
          file: values.imagePublicUrl,
        });
      }

      const body = deepDifference<StyleItem>(values, defaultValues);
      await updateStyleMutation.mutateAsync({
        ...body,
        id: params.id,
      });
      toast.success(`Style “${values.name}” updated successfully!`);
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <LoadingContainer loading={isFetching}>
      <StyleForm editMode onSubmit={updateModel} defaultValues={defaultValues}>
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Styles}
          actions={[
            <Button
              key="save-style"
              type="submit"
              data-testid="save-style-button"
              isLoading={
                updateStyleMutation.isPending ||
                getUploadUrlDataMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      </StyleForm>
    </LoadingContainer>
  );
};
